import { useState } from 'react';
import { Icon } from '../components/ui';
import { useAuth } from '../lib/auth';
import type { MfaMethod } from '../lib/types';
import { AddFactor, RecoveryCodes } from './Security';
import './Login.css';
import './shared.css';

const CHOICES: Array<{
  method: MfaMethod;
  title: string;
  detail: string;
  recommended?: boolean;
}> = [
  {
    method: 'totp',
    title: 'Authenticator app',
    detail:
      'A code from 1Password, Google Authenticator, Authy or similar. Works without a signal, and cannot be taken by someone who takes over your phone number.',
    recommended: true,
  },
  {
    method: 'sms',
    title: 'Text message',
    detail: 'A code sent to your mobile. Simple, and a good second string.',
  },
  {
    method: 'email',
    title: 'Email code',
    detail: 'A code to your inbox, for when you have no phone to hand.',
  },
];

/**
 * The screen a new account is held on until it has a second factor.
 *
 * It is not a prompt that can be dismissed: the API refuses every other route
 * until a factor is confirmed, so this is the only thing a fresh session can
 * do. All three are offered because the point is that nobody is ever locked
 * out of one route in — an app, a phone, a mailbox, and codes on paper behind
 * all three.
 */
export function MfaSetup() {
  const { user, refreshUser, signOut } = useAuth();
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  return (
    <div className="login">
      <section className="login__panel">
        <div className="login__inner">
          <div className="login__brand">
            <span className="login__mark">{Icon.logo({ size: 18 })}</span>
            <span className="login__brandname">Fairmount</span>
          </div>

          {codes ? (
            <div className="login__form">
              <h1 className="login__title">Save your recovery codes</h1>
              <RecoveryCodes
                codes={codes}
                onAcknowledge={() => {
                  // Clearing the method matters: leaving it set would remount
                  // the enrolment form and start a second enrolment behind the
                  // scenes.
                  setCodes(null);
                  setMethod(null);
                  void refreshUser();
                }}
              />
            </div>
          ) : method ? (
            <div className="login__form">
              <button
                type="button"
                className="linkbtn linkbtn--back"
                onClick={() => setMethod(null)}
              >
                {Icon.chevronLeft({ size: 13 })} Choose a different method
              </button>
              <h1 className="login__title">
                {CHOICES.find((c) => c.method === method)?.title}
              </h1>
              <AddFactor
                method={method}
                onCancel={() => setMethod(null)}
                onDone={(recoveryCodes) => {
                  if (recoveryCodes) setCodes(recoveryCodes);
                  else void refreshUser();
                }}
              />
            </div>
          ) : (
            <div className="login__form">
              <h1 className="login__title">Add a second factor</h1>
              <p className="login__lede">
                {user?.name ? `${user.name.split(' ')[0]}, this` : 'This'} portal
                holds merchant banking details, so a password alone is not enough
                to get in. Pick one to start with — you can add the others
                afterwards, and you will get recovery codes either way.
              </p>

              <div className="stack" style={{ gap: 10 }}>
                {CHOICES.map((choice) => (
                  <button
                    key={choice.method}
                    type="button"
                    className="choice"
                    onClick={() => setMethod(choice.method)}
                  >
                    <span className="choice__head">
                      <strong>{choice.title}</strong>
                      {choice.recommended && (
                        <span className="choice__tag">Recommended</span>
                      )}
                    </span>
                    <span className="choice__detail">{choice.detail}</span>
                  </button>
                ))}
              </div>

              <p className="login__footnote">
                Signed in as {user?.email}.{' '}
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() => void signOut()}
                >
                  Not you?
                </button>
              </p>
            </div>
          )}
        </div>
      </section>

      <aside className="login__aside">
        <div className="login__asideinner">
          <h2 className="login__headline">
            Two factors, three ways
            <br />
            to prove it.
          </h2>
          <ul className="login__points">
            <li>{Icon.check({ size: 15 })} An app, a text, or an email code</li>
            <li>{Icon.check({ size: 15 })} Recovery codes behind all of them</li>
            <li>{Icon.check({ size: 15 })} Add more than one, switch at sign-in</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}
